import { useEffect, useMemo, useState } from 'react';
import { api } from '../api.ts';
import type {
  Category,
  Difficulty,
  DifficultySlug,
  LeaderboardResponse,
  LeaderboardScope,
} from '../../../shared/types.ts';

const SCOPES: Array<{ slug: LeaderboardScope; label: string }> = [
  { slug: 'all-time', label: 'All time' },
  { slug: 'today', label: 'Today' },
  { slug: 'week', label: 'This week' },
  { slug: 'best-streak', label: 'Best streak' },
];

interface Props {
  onOpenUser: (username: string) => void;
}

export function LeaderboardView({ onOpenUser }: Props): React.ReactElement {
  const [scope, setScope] = useState<LeaderboardScope>('all-time');
  const [categorySlug, setCategorySlug] = useState<string | null>(null);
  const [difficulty, setDifficulty] = useState<DifficultySlug | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [difficulties, setDifficulties] = useState<Difficulty[]>([]);
  const [data, setData] = useState<LeaderboardResponse | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    void api.categories().then(setCategories);
    void api.difficulties().then(setDifficulties);
  }, []);

  useEffect(() => {
    setLoading(true);
    void api
      .leaderboard({
        scope,
        category: categorySlug ?? undefined,
        difficulty: difficulty ?? undefined,
        limit: 50,
        // The Ranking page is the public, persistent leaderboard — anonymous browsers can't be
        // linked to or compared across devices, so we restrict it to registered accounts.
        onlyUsers: true,
      })
      .then(setData)
      .finally(() => setLoading(false));
  }, [scope, categorySlug, difficulty]);

  const headerLabel = useMemo(() => {
    if (scope === 'best-streak') return 'Streak';
    return 'Score';
  }, [scope]);

  return (
    <div className="lb-page">
      <h2>Ranking</h2>
      <div className="tabs">
        {SCOPES.map((s) => (
          <button
            key={s.slug}
            className={`tab ${scope === s.slug ? 'active' : ''}`}
            onClick={() => setScope(s.slug)}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="filters">
        <select
          value={categorySlug ?? ''}
          onChange={(e) => setCategorySlug(e.target.value || null)}
          disabled={scope === 'best-streak'}
        >
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c.id} value={c.slug}>
              {c.emoji} {c.name}
            </option>
          ))}
        </select>
        <select
          value={difficulty ?? ''}
          onChange={(e) => setDifficulty((e.target.value as DifficultySlug) || null)}
          disabled={scope === 'best-streak'}
        >
          <option value="">All difficulties</option>
          {difficulties.map((d) => (
            <option key={d.id} value={d.slug}>
              {d.name}
            </option>
          ))}
        </select>
        <span className="muted small grow ta-right">
          {data ? `${data.totalRanked} ranked` : ''}
        </span>
      </div>

      <div className="lb-table card">
        <div className="lb-head">
          <span>#</span>
          <span>Player</span>
          <span className="ta-right">{headerLabel}</span>
          <span className="ta-right">Acc</span>
          <span className="ta-right">Best</span>
          <span className="ta-right">Seen</span>
        </div>
        {loading && <div className="lb-empty muted">Loading…</div>}
        {!loading && data && data.entries.length === 0 && (
          <div className="lb-empty muted">No players match this slice yet — keep playing!</div>
        )}
        {data?.entries.map((e) => {
          const clickable = !!e.username;
          return (
            <div
              key={`${e.browserId}-${e.userId}-${e.rank}`}
              className={`lb-row ${e.isYou ? 'me' : ''} ${clickable ? 'clickable' : ''}`}
              onClick={clickable ? () => onOpenUser(e.username!) : undefined}
              role={clickable ? 'button' : undefined}
              tabIndex={clickable ? 0 : undefined}
              onKeyDown={
                clickable
                  ? (ev) => {
                      if (ev.key === 'Enter' || ev.key === ' ') {
                        ev.preventDefault();
                        onOpenUser(e.username!);
                      }
                    }
                  : undefined
              }
            >
              <span className="lb-rank">#{e.rank}</span>
              <span className="lb-player">
                <span className="lb-avatar" style={{ background: e.avatarColor }}>
                  {e.displayName.slice(0, 1).toUpperCase()}
                </span>
                <span className="lb-name">{e.displayName}{e.isYou ? ' (you)' : ''}</span>
              </span>
              <span className="ta-right">
                {scope === 'best-streak' ? e.bestStreak : e.totalScore}
              </span>
              <span className="ta-right">{e.accuracyPct}%</span>
              <span className="ta-right">{e.bestStreak}</span>
              <span className="ta-right">{e.questionsSeen}</span>
            </div>
          );
        })}
        {data?.yourEntry && !data.entries.some((e) => e.isYou) && (
          <>
            <div className="lb-sep">· · ·</div>
            <div
              className={`lb-row me ${data.yourEntry.username ? 'clickable' : ''}`}
              onClick={data.yourEntry.username ? () => onOpenUser(data.yourEntry!.username!) : undefined}
              role={data.yourEntry.username ? 'button' : undefined}
              tabIndex={data.yourEntry.username ? 0 : undefined}
            >
              <span className="lb-rank">#{data.yourEntry.rank}</span>
              <span className="lb-player">
                <span className="lb-avatar" style={{ background: data.yourEntry.avatarColor }}>
                  {data.yourEntry.displayName.slice(0, 1).toUpperCase()}
                </span>
                <span className="lb-name">{data.yourEntry.displayName} (you)</span>
              </span>
              <span className="ta-right">
                {scope === 'best-streak' ? data.yourEntry.bestStreak : data.yourEntry.totalScore}
              </span>
              <span className="ta-right">{data.yourEntry.accuracyPct}%</span>
              <span className="ta-right">{data.yourEntry.bestStreak}</span>
              <span className="ta-right">{data.yourEntry.questionsSeen}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
